/**
 * useAsyncState Hook with Ref Pattern Implementation
 * 
 * A custom React hook for managing async operations with proper loading, error, and data states.
 * This implementation uses the ref pattern to eliminate dependency instability and prevent infinite loops.
 * 
 * ## Key Features:
 * - **Ref Pattern**: Uses useRef to store asyncFunction reference, eliminating dependency issues
 * - **Stable execute callback**: Always has empty dependency array for consistent reference
 * - **Performance optimized**: Prevents unnecessary re-renders from function changes
 * - **Error handling**: Comprehensive error handling with typed error states
 * - **Memory safe**: Proper cleanup and state management
 * 
 * ## Ref Pattern Benefits:
 * - **Eliminates dependency instability**: execute callback never changes reference
 * - **Always uses latest function**: asyncFunctionRef.current always points to latest function
 * - **Better performance**: Prevents unnecessary re-creations of execute callback
 * - **More resilient**: Works seamlessly with dynamic function changes
 * - **React best practices**: Follows recommended patterns for hook optimization
 * 
 * ## Technical Implementation:
 * ```typescript
 * // Store function reference in ref (doesn't trigger re-renders)
 * const asyncFunctionRef = useRef(asyncFunction);
 * 
 * // Update ref when function changes (without triggering execute re-creation)
 * useEffect(() => {
 *   asyncFunctionRef.current = asyncFunction;
 * }, [asyncFunction]);
 * 
 * // Stable execute callback with empty dependency array
 * const execute = useCallback(async (...args) => {
 *   const result = await asyncFunctionRef.current(...args); // Always latest function
 *   return result;
 * }, []); // Empty deps = stable reference
 * ```
 * 
 * ## Usage Example:
 * ```typescript
 * // Context Provider
 * const fetchData = useCallback(async (filters) => {
 *   return await api.getData(filters);
 * }, []);
 * 
 * const { state, execute } = useAsyncState(fetchData);
 * 
 * // Component
 * useEffect(() => {
 *   execute(filters); // execute reference never changes = no infinite loops
 * }, [execute, filters]); // Safe to depend on execute
 * ```
 * 
 * ## Architecture Integration:
 * - **Enhanced core hook**: Improves the foundational useAsyncState used throughout the app
 * - **Works with existing fixes**: Seamlessly integrates with Phase 1 stable function fixes
 * - **No breaking changes**: Maintains exact same API as before
 * - **Future-proof**: Protects against hook dependency issues in complex components
 * 
 * @author FinStack Development Team
 * @since Version with ref pattern implementation
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { ApiError } from '../services/api';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export interface UseAsyncStateReturn<T> {
  state: AsyncState<T>;
  execute: (...args: any[]) => Promise<T>;
  reset: () => void;
  setData: (data: T) => void;
}

/**
 * Hook for managing async operations with proper loading and error states
 * 
 * Uses the ref pattern to eliminate dependency instability in the execute callback.
 * The asyncFunction is stored in a ref and updated via useEffect, ensuring the execute
 * callback maintains a stable reference while always calling the latest function version.
 * 
 * @template T - The type of data returned by the async function
 * @param asyncFunction - The async function to execute (optional)
 * @param initialData - Initial data value (defaults to null)
 * @returns Object containing state, execute function, reset function, and setData function
 * 
 * @example
 * ```typescript
 * // Basic usage
 * const { state, execute } = useAsyncState(async (id: string) => {
 *   return await api.getItem(id);
 * });
 * 
 * // Execute the function
 * const handleClick = () => execute('item-123');
 * 
 * // Access state
 * if (state.loading) return <Spinner />;
 * if (state.error) return <Error message={state.error} />;
 * if (state.data) return <DisplayData data={state.data} />;
 * ```
 */
export function useAsyncState<T>(
  asyncFunction?: (...args: any[]) => Promise<T>,
  initialData: T | null = null
): UseAsyncStateReturn<T> {
  const [state, setState] = useState<AsyncState<T>>({
    data: initialData,
    loading: false,
    error: null
  });

  // REF PATTERN IMPLEMENTATION
  // Store asyncFunction in ref to avoid dependency issues in execute callback
  // This prevents the execute callback from being recreated when asyncFunction changes
  const asyncFunctionRef = useRef(asyncFunction);
  
  // Update ref when function changes (without triggering execute callback re-creation)
  // This useEffect ensures asyncFunctionRef.current always points to the latest function
  // while keeping the execute callback stable with empty dependency array
  useEffect(() => {
    asyncFunctionRef.current = asyncFunction;
  }, [asyncFunction]);

  // STABLE EXECUTE CALLBACK
  // This callback has an empty dependency array, making it stable across re-renders
  // It always calls asyncFunctionRef.current which points to the latest function
  // This pattern eliminates infinite loops caused by function dependency instability
  const execute = useCallback(async (...args: any[]): Promise<T> => {
    if (!asyncFunctionRef.current) {
      throw new Error('No async function provided');
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Always call the latest function via ref (asyncFunctionRef.current)
      const result = await asyncFunctionRef.current(...args);
      setState({ data: result, loading: false, error: null });
      return result;
    } catch (error) {
      const errorMessage = error instanceof ApiError 
        ? error.message 
        : error instanceof Error 
          ? error.message 
          : 'An unexpected error occurred';
      
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      throw error;
    }
  }, []); // Empty dependency array = stable function reference (KEY to preventing infinite loops)

  const reset = useCallback(() => {
    setState({ data: initialData, loading: false, error: null });
  }, [initialData]);

  const setData = useCallback((data: T) => {
    setState(prev => ({ ...prev, data }));
  }, []);

  return { state, execute, reset, setData };
}

/**
 * Hook specifically for managing lists with CRUD operations
 */
export function useAsyncList<T>(
  fetchFunction: () => Promise<T[]>,
  initialData: T[] = []
) {
  const { state, execute: fetchItems, reset, setData: setDataInternal } = useAsyncState(fetchFunction, initialData);

  // Create a specialized setData function for arrays
  const setData = useCallback((newData: T[]) => {
    setDataInternal(newData);
  }, [setDataInternal]);

  const addItem = useCallback((item: T) => {
    const currentData = state.data || [];
    setData([...currentData, item]);
  }, [state.data, setData]);

  const updateItem = useCallback((updatedItem: T, idSelector: (item: T) => string | number) => {
    const currentData = state.data || [];
    const updatedData = currentData.map((item: T) => 
      idSelector(item) === idSelector(updatedItem) ? updatedItem : item
    );
    setData(updatedData);
  }, [state.data, setData]);

  const removeItem = useCallback((itemId: string | number, idSelector: (item: T) => string | number) => {
    const currentData = state.data || [];
    const filteredData = currentData.filter((item: T) => idSelector(item) !== itemId);
    setData(filteredData);
  }, [state.data, setData]);

  const removeItems = useCallback((itemIds: (string | number)[], idSelector: (item: T) => string | number) => {
    const currentData = state.data || [];
    const idSet = new Set(itemIds);
    const filteredData = currentData.filter((item: T) => !idSet.has(idSelector(item)));
    setData(filteredData);
  }, [state.data, setData]);

  return {
    state,
    fetchItems,
    reset,
    setData,
    addItem,
    updateItem,
    removeItem,
    removeItems
  };
}